import React, { useState } from "react";
import { submitQuote } from "../../lib/api";
import { ArrowRight, Mail, Phone, MapPin, Check } from "lucide-react";
import { toast } from "sonner";

const INDUSTRIES = [
  "Restaurant / Café",
  "Gym / Fitness",
  "Real Estate",
  "Corporate / Office",
  "Event / Activation",
  "Hotel / Hospitality",
  "School / College",
  "Healthcare",
  "Other",
];

const QUANTITIES = [
  "1 skid (1,728 bottles)",
  "2-5 skids",
  "5-10 skids",
  "10+ skids",
  "Not sure yet",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    industry: "",
    quantity: "",
    use_case: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await submitQuote(form);
      toast.success("Quote request sent. We'll be in touch within 24 hours.");
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Email hello@tactiq.ca or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 bg-[var(--tq-bg)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left: pitch */}
          <div className="lg:col-span-5">
            <div className="overline mb-4">Request a Quote</div>
            <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tighter leading-[0.9]">
              Let&apos;s build your
              <br />
              <span className="font-editorial italic font-medium">
                walking billboard.
              </span>
            </h2>
            <p className="mt-6 text-lg text-slate-600 max-w-md">
              Tell us a little about your brand and what you want to do. We&apos;ll send
              a digital mockup and a custom quote — usually within 24 hours.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-[var(--tq-navy)] text-white flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} />
                </div>
                <div>
                  <div className="overline mb-1">Studio</div>
                  <div className="text-slate-900 font-medium">
                    2795 Slough St, Mississauga,
                    <br />
                    ON L4T 1G2, Canada
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-[var(--tq-navy)] text-white flex items-center justify-center flex-shrink-0">
                  <Phone size={18} />
                </div>
                <div>
                  <div className="overline mb-1">Talk to us</div>
                  <div className="text-slate-900">
                    <a href="tel:+16472001753" className="font-medium hover:underline" data-testid="contact-phone-ash">
                      Ash Tandon · 647-200-1753
                    </a>
                    <br />
                    <a href="tel:+14168562809" className="font-medium hover:underline" data-testid="contact-phone-aaishwari">
                      Aaishwari Chouhan · 416-856-2809
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-[var(--tq-navy)] text-white flex items-center justify-center flex-shrink-0">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="overline mb-1">Email</div>
                  <a
                    href="mailto:hello@tactiq.ca"
                    data-testid="contact-email"
                    className="text-slate-900 font-medium hover:underline"
                  >
                    hello@tactiq.ca
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-10 shadow-sm">
              {done ? (
                <div data-testid="quote-success" className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-6">
                    <Check size={28} />
                  </div>
                  <h3 className="font-display text-3xl font-black tracking-tight mb-3">
                    Thanks — we&apos;ve got it.
                  </h3>
                  <p className="text-slate-600 max-w-sm mx-auto">
                    Ash or Aaishwari will reach out within 24 hours with a mockup
                    and pricing. In the meantime, you&apos;ll hear from{" "}
                    <b>hello@tactiq.ca</b>.
                  </p>
                  <button
                    onClick={() => {
                      setDone(false);
                      setForm({
                        name: "", email: "", company: "", phone: "",
                        industry: "", quantity: "", use_case: "", message: "",
                      });
                    }}
                    className="tq-btn tq-btn-ghost mt-8"
                    data-testid="quote-send-another"
                  >
                    Send another request
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="Your Name *"
                      testid="quote-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Alex Morgan"
                      required
                    />
                    <Field
                      label="Email *"
                      type="email"
                      testid="quote-email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="alex@yourbrand.com"
                      required
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="Company"
                      testid="quote-company"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Maison Bramalea"
                    />
                    <Field
                      label="Phone"
                      type="tel"
                      testid="quote-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(647) 555-0199"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <SelectField
                      label="Industry"
                      testid="quote-industry"
                      value={form.industry}
                      onChange={update("industry")}
                      options={INDUSTRIES}
                    />
                    <SelectField
                      label="Quantity needed"
                      testid="quote-quantity"
                      value={form.quantity}
                      onChange={update("quantity")}
                      options={QUANTITIES}
                    />
                  </div>

                  <Field
                    label="Use case / campaign"
                    testid="quote-usecase"
                    value={form.use_case}
                    onChange={update("use_case")}
                    placeholder="e.g. grand opening, trade show, closing gift"
                  />

                  <div>
                    <label className="block">
                      <span className="overline">Anything else?</span>
                      <textarea
                        data-testid="quote-message"
                        value={form.message}
                        onChange={update("message")}
                        rows={4}
                        placeholder="Tell us about your brand, timeline, special requests…"
                        className="w-full mt-3 px-4 py-3 border border-slate-200 rounded-xl focus:border-[var(--tq-navy)] focus:ring-2 focus:ring-[var(--tq-navy)]/20 outline-none transition resize-none"
                      />
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="quote-submit"
                    className="tq-btn tq-btn-primary w-full justify-center text-base py-4 disabled:opacity-50"
                  >
                    {loading ? "Sending…" : "Send Request"}
                    <ArrowRight size={16} />
                  </button>

                  <p className="text-xs text-slate-500 text-center">
                    By submitting, you agree to be contacted by Tactiq Experience
                    about your inquiry. We never share your data.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, ...rest }) => (
  <label className="block">
    <span className="overline">{label}</span>
    <input
      data-testid={testid}
      {...rest}
      className="w-full mt-3 px-4 py-3 border border-slate-200 rounded-xl focus:border-[var(--tq-navy)] focus:ring-2 focus:ring-[var(--tq-navy)]/20 outline-none transition"
    />
  </label>
);

const SelectField = ({ label, testid, value, onChange, options }) => (
  <label className="block">
    <span className="overline">{label}</span>
    <select
      data-testid={testid}
      value={value}
      onChange={onChange}
      className="w-full mt-3 px-4 py-3 border border-slate-200 rounded-xl focus:border-[var(--tq-navy)] focus:ring-2 focus:ring-[var(--tq-navy)]/20 outline-none transition bg-white"
    >
      <option value="">Select…</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </label>
);
